<template>
  <div class="emergency-history-view">
    <!-- 查询栏 -->
    <div class="compact-query-bar">
      <div class="query-section">
        <!-- 状态提示徽章 -->
        <div :class="['status-badge-tip', robotStatus === 'online' ? 'online-badge' : 'offline-badge']">
          <Icon :type="robotStatus === 'online' ? 'ios-checkmark-circle' : 'ios-close-circle'" />
          <span class="badge-text">
            {{ robotStatus === 'online' ? langMap.dashboard_onlineTip[lang] : langMap.dashboard_offlineTip[lang] }}
          </span>
        </div>
        
        <!-- 紧急通知类型切换 -->
        <div class="monitor-type-tabs">
          <div
            v-for="type in emergencyTypes"
            :key="type.value"
            :class="['type-tab', { active: emergencyType === type.value }]"
            @click="handleEmergencyTypeChange(type.value)"
          >
            <span class="tab-icon">
              <Icon v-if="typeof type.icon === 'string' && type.icon.includes('ios-')" :type="type.icon" />
              <template v-else>{{ type.icon }}</template>
            </span>
            <span class="tab-text">{{ langMap[type.label][lang] }}</span>
          </div>
        </div>
      </div>
      
      <div class="query-filters">
        <div class="filter-row">
          <!-- 时间范围 -->
          <div class="filter-item">
            <span class="filter-label">{{ langMap.dashboard_timeRange[lang] }}</span>
            <DatePicker
              v-model="timeRange"
              type="datetimerange"
              :placeholder="langMap.dashboard_selectTimeRangePlaceholder[lang]"
              format="yyyy-MM-dd HH:mm"
              :options="dateOptions"
              style="width: 340px"
              size="small"
            />
          </div>

          <!-- 告警类型 -->
          <div class="filter-item" v-if="emergencyType === 'alert'">
            <span class="filter-label">{{ langMap.alertInfoType[lang] }}</span>
            <Select
              v-model="queryParams.type"
              :placeholder="langMap.alertInfoType[lang]"
              style="width: 160px"
              size="small"
            >
              <Option
                v-for="type in alertTypeOptions"
                :key="type.value"
                :value="type.value"
              >
                {{ langMap[type.label][lang] }}
              </Option>
            </Select>
          </div>
          
          <!-- 查询按钮 -->
          <div class="filter-actions">
            <Button type="primary" size="small" @click="handleQuery(true)" :loading="loading">
              <Icon type="ios-search" /> {{ langMap.dashboard_search[lang] }}
            </Button>
            <Button size="small" @click="handleReset">
              <Icon type="ios-refresh" /> {{ langMap.dashboard_reset[lang] }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 数据展示区域 -->
    <emergency-history-table
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      :history-type="emergencyType"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />
  </div>
</template>

<script>
import api from '@/api'
import { buildQueryParams } from '@/libs/util'
import langMap from '@/utils/language'
import * as timeUtil from '@/utils/dateTimeUtil'
import { mapState } from 'vuex';
import EmergencyHistoryTable from './EmergencyHistoryTable.vue';

export default {
  name: 'EmergencyHistoryView',
  components: { EmergencyHistoryTable },
  props: {
    robotCode: {
      type: String,
      required: true
    },
    robotStatus: {
      type: String,
      default: 'offline' // 'online' 或 'offline'
    }
  },
  data() {
    return {
      loading: false,
      langMap: langMap,
      timeRange: [],
      hasData: false,
      emptyMessage: '',
      emergencyType: 'emergency', // 默认只显示紧急通知
      emergencyTypes: [
        { value: 'emergency', label: 'emergencyNotificationTitle', icon: 'ios-warning' },
        { value: 'alert', label: 'alertInfoNotificationTitle', icon: 'ios-alert' }
      ],
      alertTypeOptions: [
        { value: 'all', label: 'all' },
        { value: 1, label: 'alertInfoObstacleAvoidanceFailed' },
        { value: 2, label: 'alertInfoBehaviorProhibitionFailed' }
      ],
      queryParams: {
        robotCode: this.robotCode,
        startTime: null,
        endTime: null,
        index: 1,
        size: 20,
        type: 'all'
      },
      tableData: [],
      pagination: {
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      },
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    dateOptions() {
      return {
        disabledDate: (date) => {
          // 禁用未来时间
          const now = new Date();
          if (date > now) {
            return true;
          }

          // 禁用2个月以前的时间
          const twoMonthsAgo = new Date();
          twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
          if (date < twoMonthsAgo) {
            return true;
          }

          // 如果已经选择了开始或结束时间，限制范围为7天
          if (this.timeRange && this.timeRange.length > 0) {
            const selectedDate = this.timeRange[0];
            if (selectedDate) {
              const maxDiff = 7 * 24 * 3600 * 1000; // 7天的毫秒数
              const diff = Math.abs(date.getTime() - selectedDate.getTime());
              if (diff > maxDiff) {
                return true;
              }
            }
          }

          return false;
        },
        shortcuts: [
          {
            text: this.langMap.dashboard_last1Hour[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last3Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 3);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last6Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 6);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last24Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last7Days[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              return [start, end];
            },
          },
        ],
      };
    },
  },
  watch: {
    robotCode(newCode) {
      this.queryParams.robotCode = newCode;
      this.handleReset();
    },
    timeRange(newVal) {
      // 当 timeRange 通过 v-model 变化时，重新计算时间戳
      if (newVal && newVal.length === 2) {
        this.handleTimeChange(newVal);
      } else {
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    }
  },
  mounted() {
    // 默认设置最近3小时（不自动查询，需手动点击查询）
    this.emptyMessage = this.langMap.dashboard_selectTimeAndSearch[this.lang];
    const end = new Date();
    const start = new Date();
    start.setTime(start.getTime() - 3600 * 1000 * 3);
    this.timeRange = [start, end];
    this.handleTimeChange(this.timeRange);
  },
  methods: {
    handleTimeChange(dates) {
      if (dates && dates.length === 2) {
        // 验证时间范围是否超过7天
        const diff = dates[1].getTime() - dates[0].getTime();
        const maxDiff = 7 * 24 * 3600 * 1000; // 7天

        if (diff > maxDiff) {
          this.$Message.warning(this.langMap.dashboard_timeRangeMaxOneWeek[this.lang]);
          this.timeRange = [];
          this.queryParams.startTime = null;
          this.queryParams.endTime = null;
          return;
        }

        this.queryParams.startTime = dates[0].getTime();
        this.queryParams.endTime = dates[1].getTime();
      } else {
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    },

    handleQuery(resetPage = false) {
      if (!this.queryParams.startTime || !this.queryParams.endTime) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectTimeRange[this.lang]);
        return;
      }

      // 只有主动查询时才重置到第1页和默认每页数量
      if (resetPage) {
        this.queryParams.index = 1;
        this.queryParams.size = 20;
      }

      this.loading = true;
      this.emptyMessage = this.langMap.dashboard_querying[this.lang];

      const requestParams = { ...this.queryParams };
      if (requestParams.type === 'all') {
        delete requestParams.type;
      }
      const queryString = buildQueryParams(requestParams);
      const apiPath = this.emergencyType === 'alert'
        ? `notice-server/monitor/record/alertInfo/page?${queryString}`
        : `notice-server/monitor/record/emergency/page?${queryString}`;

      api.getData(apiPath, this).then(res => {
        this.loading = false;
        if (res.state !== 0) {
          this.tableData = [];
          const pageData = res.data || {};
          const rows = pageData.rows || [];
          if (this.emergencyType === 'alert') {
            rows.forEach(item=>{
              this.tableData.push({
                dataTime: item.dataTime ? timeUtil.formatTime(item.dataTime) : '',
                type: item.type,
                message: item.message || '',
                robotCode: item.robotCode || this.robotCode
              })
            })
          } else {
            rows.forEach(item=>{
              const data = item.data ? JSON.parse(item.data) : {}
              this.tableData.push({
                createTime:item.createTime,
                ...data,
                robotCode : item.robotCode
              })
            })
          }
          this.pagination = {
            total: pageData.total || 0,
            current: pageData.current || this.queryParams.index,
            size: pageData.size || this.queryParams.size,
            pages: pageData.pages || 0
          };
          this.hasData = this.tableData.length > 0;
          if (!this.hasData) {
            this.emptyMessage = this.langMap.empty[this.lang];
          }
        } else {
          this.hasData = false;
          this.tableData = [];
          this.emptyMessage = res.msg || this.langMap.dashboard_queryFailed[this.lang];
          this.$Message.error(res.msg || this.langMap.dashboard_queryFailed[this.lang]);
        }
      }).catch(error => {
        this.loading = false;
        this.hasData = false;
        this.tableData = [];
        this.emptyMessage = this.langMap.dashboard_queryFailed[this.lang];
        this.$Message.error(this.langMap.dashboard_queryFailed[this.lang] + ': ' + (error.message || ''));
      });
    },
    
    handlePageChange(page) {
      this.queryParams.index = page;
      this.pagination.current = page;
      this.handleQuery();
    },
    
    handlePageSizeChange(size) {
      this.queryParams.size = size;
      this.queryParams.index = 1;
      this.pagination.current = 1;
      this.handleQuery();
    },
    
    handleReset() {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 3);
      this.timeRange = [start, end];
      this.handleTimeChange(this.timeRange);
      this.queryParams.index = 1;
      this.queryParams.size = 20;
      this.queryParams.type = 'all';
      this.hasData = false;
      this.tableData = [];
      this.pagination = {
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      };
      this.emptyMessage = this.langMap.dashboard_selectTimeAndSearch[this.lang];
    },

    // 切换紧急通知类型
    handleEmergencyTypeChange(type) {
      if (this.emergencyType === type) return;
      this.emergencyType = type;
      this.handleReset();
    },
  }
}
</script>

<style lang="less" scoped>
.emergency-history-view {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.compact-query-bar {
  margin-bottom: 20px;
}
.query-section {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 20px;
  
  .status-badge-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 6px;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    
    /deep/ i {
      font-size: 16px;
    }
    
    .badge-text {
      color: inherit;
    }
    
    &.online-badge {
      background: linear-gradient(135deg, rgba(57, 255, 20, 0.15), rgba(76, 175, 80, 0.1));
      border: 1px solid rgba(57, 255, 20, 0.4);
      color: #39FF14;
      
      /deep/ i {
        color: #39FF14;
      }
    }
    
    &.offline-badge {
      background: linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 193, 7, 0.1));
      border: 1px solid rgba(255, 152, 0, 0.4);
      color: #FF9800;
      
      /deep/ i {
        color: #FF9800;
      }
    }
  }
  
  .monitor-type-tabs {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    
    .type-tab {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(0, 30, 60, 0.6);
      border: 1px solid rgba(0, 240, 255, 0.3);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.6);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      
      .tab-icon {
        font-size: 16px;
      }
      
      .tab-text {
        white-space: nowrap;
      }
      
      &:hover {
        background: rgba(0, 50, 80, 0.8);
        border-color: rgba(0, 240, 255, 0.6);
        color: #00f0ff;
        transform: translateY(-2px);
      }
      
      &.active {
        background: linear-gradient(135deg, rgba(244, 67, 54, 0.3), rgba(255, 87, 34, 0.2));
        border-color: #F44336;
        color: #FF5722;
        box-shadow: 0 0 12px rgba(244, 67, 54, 0.3), inset 0 0 8px rgba(244, 67, 54, 0.1);
      }
    }
  }
}
.filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.filter-label {
  margin-right: 8px;
}
</style>
